import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { NavDots } from "@/components/nav-dots";
import { HeroSection } from "@/sections/home/hero-section";
import { PathsSection } from "@/sections/home/paths-section";
import portrait from "@/assets/sylwia-portrait.jpg.asset.json";
import portraitMobile from "@/assets/sylwia-portrait-mobile.jpg";
import portraitTablet from "@/assets/sylwia-portrait-tablet.jpg";

import {
  faqItems,
  mediaArticles,
  mediaPodcasts,
  books,
  splitEvents,
} from "@/content/site-content";

import ogSocial from "@/assets/og-sylwia-sitkowska.jpg";

import {
  abs,
  websiteSchema,
  PERSON_ID,
  personSchema,
  eventSchema,
  booksListSchema,
  mediaListSchema,
} from "@/content/seo";

import { IntroSection } from "@/sections/home/intro-section";
import { ProgramSection } from "@/sections/home/program-section";
import { ResultsSection } from "@/sections/home/results-section";
import { AboutSection } from "@/sections/home/about-section";
import { OfferSection } from "@/sections/home/offer-section";
import { EventsSection } from "@/sections/home/events-section";
import { MediaSection } from "@/sections/home/media-section";
import { KnowledgeSection } from "@/sections/home/knowledge-section";
import { FaqSection } from "@/sections/home/faq-section";
import { ClosingSection } from "@/sections/home/closing-section";
import { ContactSection } from "@/sections/home/contact-section";
import { TestimonialsSection } from "@/sections/home/testimonials-section";
import { PodcastSection } from "@/sections/home/podcast-section";

const title = "Psycholog dla liderów | Sylwia Sitkowska";
const description =
  "Sylwia Sitkowska, psycholożka, psychoterapeutka CBT i przedsiębiorczyni. Psychologia biznesu, wystąpienia, mastermind i konsultacje dla liderów.";
const ogImage = abs(ogSocial);


const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqItems.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: { "@type": "Answer", text: item.a.join(" ") },
  })),
};

const organizationJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": "https://www.przystan-psychologiczna.pl/#organization",
  name: "Przystań Psychologiczna",
  url: "https://www.przystan-psychologiczna.pl/",
  description:
    "Poradnia psychologiczna założona w Warszawie w 2009 roku; dziś organizacja obejmująca 30 placówek i około 300 osób.",
  foundingDate: "2009",
  founder: { "@type": "Person", name: "Sylwia Sitkowska", "@id": PERSON_ID },
  address: {
    "@type": "PostalAddress",
    addressLocality: "Warszawa",
    addressCountry: "PL",
  },
};

const booksJsonLd = {
  "@context": "https://schema.org",
  ...booksListSchema(books),
};

const mediaJsonLd = {
  "@context": "https://schema.org",
  ...mediaListSchema([...mediaPodcasts, ...mediaArticles]),
};

const eventsJsonLd = () => ({
  "@context": "https://schema.org",
  "@graph": splitEvents().upcoming.map((event) => eventSchema(event)),
});

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://sylwiasitkowska.pl/" },
      { property: "og:image", content: ogImage },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      {
        property: "og:image:alt",
        content: "Sylwia Sitkowska, psycholog dla liderów, psychoterapeutka i przedsiębiorczyni",
      },
      { property: "og:site_name", content: "Sylwia Sitkowska" },
      { property: "og:locale", content: "pl_PL" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: ogImage },
      {
        name: "twitter:image:alt",
        content: "Sylwia Sitkowska, psycholog dla liderów, psychoterapeutka i przedsiębiorczyni",
      },
      {
        name: "keywords",
        content:
          "Sylwia Sitkowska, psycholog dla liderów, psychologia biznesu, psychologia przywództwa, wystąpienia dla firm, mastermind dla liderów, konsultacje dla przedsiębiorców",
      },
      { name: "robots", content: "index, follow, max-image-preview:large" },
    ],
    links: [
      { rel: "canonical", href: "https://sylwiasitkowska.pl/" },
      { rel: "preload", as: "image", href: portraitMobile, fetchPriority: "high", media: "(max-width: 767px)" },
      { rel: "preload", as: "image", href: portraitTablet, fetchPriority: "high", media: "(min-width: 768px) and (max-width: 1023px)" },
      { rel: "preload", as: "image", href: portrait.url, fetchPriority: "high", media: "(min-width: 1024px)" },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(personSchema) },
      { type: "application/ld+json", children: JSON.stringify(organizationJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(websiteSchema) },
      { type: "application/ld+json", children: JSON.stringify(eventsJsonLd()) },
      { type: "application/ld+json", children: JSON.stringify(booksJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(mediaJsonLd) },
    ],
  }),
  component: Index,
});

function Index() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <NavDots />
      <main id="tresc-glowna">
        <HeroSection />
        <PathsSection />
        <EventsSection />
        <IntroSection />
        <ResultsSection />
        <ProgramSection />
        <TestimonialsSection />
        <OfferSection />
        <AboutSection />
        <PodcastSection />
        <MediaSection />
        <KnowledgeSection />
        <FaqSection />
        <ClosingSection />
        <ContactSection />
      </main>

      <SiteFooter />
    </div>
  );
}
